import React, { memo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Icon, IconNames } from 'atoms';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, tablet } from 'services/ui';
import { AttendanceType } from 'components/types';

import { LessonJournalFooter } from './LessonJournalFooter';
import { JournalComment } from './JournalComment';
import { StudentsAttendance } from './StudentsAttendance';

export interface ILessonJournal {
  comment: string;
  isFinished: boolean;
  students: AttendanceType[];
  finishLessonLoading: boolean;
  success: boolean;
  isAllStudentsChecked: boolean;
  isEnded: boolean;
  hasLessonsToFinish: boolean;
  /** spec 0013: studentId → "HH:mm" join-click hints for the chips. */
  joinTimes?: Record<string, string>;
  /** ТЗ 0026: строки, чья отметка сейчас сохраняется. */
  savingIds?: string[];
  /** spec 0013: отметить студентов, которые заходили по кнопке. */
  onMarkJoined?: () => void;
  onCheckAllStudents: () => void;
  onCheckStudent: (attendanceId: string, next: boolean) => void;
  onChangeComment: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  className?: string;
}

export const LessonJournal = memo(
  ({
    isFinished,
    students,
    comment,
    finishLessonLoading,
    isAllStudentsChecked,
    isEnded,
    hasLessonsToFinish,
    joinTimes,
    savingIds,
    onMarkJoined,
    onCheckAllStudents,
    onCheckStudent,
    onChangeComment,
    onSubmit,
    onCancel,
    className,
    success,
  }: ILessonJournal) => {
    const { t } = useTranslation();
    // ТЗ 0026: занятие с проставленной посещаемостью, но не закрытое, — это
    // новый и невидимый раньше разрыв. `finished` не украшение: он и
    // знаменатель сводки посещаемости в ERP, и множитель выплаты
    // преподавателю. Пока галочки уезжали только вместе с кнопкой, такого
    // состояния существовать не могло.
    const hasMarks = students.some(({ isUnmarked }) => !isUnmarked);
    const showUnclosedWarning = !isFinished && isEnded && hasMarks;

    return (
      <LessonJournalContainer className={className}>
        {showUnclosedWarning && (
          <AttentionMessage>
            <IconStyled name={IconNames.WarningYellow} />
            <div>
              <AttentionBold>{t('lesson.attention')}</AttentionBold>
              {t('lesson.journal.savedButNotFinished')}
            </div>
          </AttentionMessage>
        )}
        {hasLessonsToFinish && !isFinished && isEnded && !showUnclosedWarning && (
          <AttentionMessage>
            <IconStyled name={IconNames.WarningYellow} />
            <div>
              <AttentionBold>{t('lesson.attention')}</AttentionBold>
              {t('lesson.needToFinish')}
            </div>
          </AttentionMessage>
        )}
        {hasLessonsToFinish && !isFinished && !isEnded && (
          <AttentionMessage>
            <IconStyled name={IconNames.WarningYellow} />
            <div>
              <AttentionBold>{t('lesson.attention')}</AttentionBold>
              {t('lesson.haveUnfinished')}
            </div>
          </AttentionMessage>
        )}

        <StudentsAttendance
          students={students}
          isAllStudentsChecked={isAllStudentsChecked}
          joinTimes={joinTimes}
          savingIds={savingIds}
          onMarkJoined={onMarkJoined}
          onCheckAllStudents={onCheckAllStudents}
          onCheckStudent={onCheckStudent}
        />

        <JournalCommentStyled
          comment={comment}
          isFinished={isFinished}
          onChangeComment={onChangeComment}
        />

        <LessonJournalFooterStyled
          isFinished={isFinished}
          onSubmit={onSubmit}
          onCancel={onCancel}
          finishLessonLoading={finishLessonLoading}
          success={success}
        />
      </LessonJournalContainer>
    );
  },
);

const LessonJournalContainer = styled.div`
  width: 100%;
`;

const JournalCommentStyled = styled(JournalComment)<{ isFinished: boolean }>`
  margin: ${({ isFinished }) => (isFinished ? '32px 0 78px' : '32px 0 212px')};

  ${tablet} {
    margin: 40px 0 80px;
  }

  ${desktop} {
    margin: 60px 0 0;
  }
`;

const LessonJournalFooterStyled = styled(LessonJournalFooter)`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
`;

const AttentionMessage = styled.div`
  display: flex;
  padding: 5px 12px;
  margin-bottom: 32px;
  border-radius: 4px;
  border: 1px solid #fdd32a;
  background: rgba(255, 218, 23, 0.08);
  ${fontService.text({ type: TextType.small })};

  ${tablet} {
    width: max-content;
    align-items: center;
    ${fontService.text({ type: TextType.normal })};
  }
`;

const AttentionBold = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })};

  ${tablet} {
    ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })};
  }
`;

const IconStyled = styled((props) => <Icon {...props} />)`
  margin-right: 4px;
  width: 16px;
  height: 16px;

  ${tablet} {
    width: 20px;
    height: 20px;
  }
`;
